import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import Footer from '@/components/Footer';

function Section({ title, children }) {
  return (
    <div className="space-y-2">
      <h2 className="font-heading font-bold text-base text-[#48293A]">{title}</h2>
      <div className="text-sm text-[#4C4D57] leading-relaxed space-y-2">{children}</div>
    </div>
  );
}

export default function PrivacidadPage() {
  return (
    <div className="min-h-screen bg-[#FBF8F3] flex flex-col">
      <div className="flex-1 max-w-2xl mx-auto w-full px-4 py-8 space-y-7">
        <div className="flex items-center gap-3">
          <Link to="/" className="p-2 rounded-full hover:bg-[#48293A]/8 transition-colors text-[#48293A]">
            <ChevronLeft size={20} />
          </Link>
          <img src="/logo.png" alt="El Club de Manri" className="h-8 w-auto" />
        </div>

        <div>
          <h1 className="font-heading font-bold text-2xl text-[#48293A]">Política de Privacidad</h1>
          <p className="text-xs text-[#4C4D57]/60 mt-1">Última actualización: 18 de junio de 2026</p>
        </div>

        <Section title="1. Responsable del tratamiento">
          <p>
            El Club de Manri es un servicio operado por <strong>18 Innovación Educativa, S.L.</strong> (CIF B70848882),
            con domicilio en Calle Los Calados, Edificio Alondras, 1, 38111 Santa Cruz de Tenerife,
            que actúa como responsable del tratamiento de tus datos.
          </p>
          <p><strong>Contacto:</strong> info@elclubdemanri.es</p>
        </Section>

        <Section title="2. ¿Qué datos tratamos y de quién?">
          <p>A través de la aplicación El Club de Manri tratamos:</p>
          <p><strong>Del padre, madre o tutor/a (persona que registra a la familia):</strong></p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Nombre y apellidos.</li>
            <li>DNI/NIE.</li>
            <li>Correo electrónico.</li>
            <li>Número de teléfono móvil (verificado mediante un código por WhatsApp).</li>
          </ul>
          <p><strong>De los menores inscritos:</strong></p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Nombre y edad.</li>
            <li>Imágenes (fotografías o vídeos), únicamente si se ha otorgado el consentimiento de imagen (ver punto 6).</li>
          </ul>
          <p><strong>Datos de la actividad:</strong></p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Zona/ubicación dentro del Parque Marítimo.</li>
            <li>Actividades y talleres en los que se inscribe a los menores.</li>
            <li>Registro de los consentimientos y declaraciones otorgados.</li>
          </ul>
          <p>No solicitamos datos de salud ni otras categorías especiales de datos.</p>
        </Section>

        <Section title="3. Finalidades">
          <ul className="list-disc pl-5 space-y-1">
            <li>Gestionar la inscripción y participación de los menores en las actividades de ocio infantil de El Club de Manri en el Parque Marítimo César Manrique.</li>
            <li>Verificar el contacto de la familia mediante un código de un solo uso enviado por WhatsApp.</li>
            <li>Controlar el acceso a las actividades (sistema de códigos QR).</li>
            <li>Comunicarnos con las familias sobre las actividades.</li>
            <li>En su caso, y solo con consentimiento, usar imágenes de las actividades.</li>
          </ul>
        </Section>

        <Section title="4. Base jurídica">
          <ul className="list-disc pl-5 space-y-1">
            <li>El <strong>consentimiento</strong> del padre, madre o tutor/a (art. 6.1.a RGPD), que actúa también en representación del menor.</li>
            <li>La <strong>ejecución de la relación</strong> derivada de la inscripción (art. 6.1.b RGPD).</li>
            <li>Para los <strong>datos de los menores</strong>, el consentimiento de quien ostenta la patria potestad o tutela (art. 7 LOPDGDD).</li>
          </ul>
        </Section>

        <Section title="5. Datos de menores">
          <p>
            Los datos de los menores se tratan exclusivamente con el consentimiento y bajo la responsabilidad
            del padre, madre o tutor/a, quien declara estar legitimado para otorgarlo. Si detectamos que se han
            facilitado datos de un menor sin dicho consentimiento, los suprimiremos.
          </p>
        </Section>

        <Section title="6. Uso de imágenes">
          <p>
            El uso de fotografías o vídeos en los que aparezcan menores requiere un <strong>consentimiento
            específico y separado</strong>, que el padre, madre o tutor/a puede otorgar o denegar durante el
            registro y <strong>revocar en cualquier momento</strong> escribiendo a info@elclubdemanri.es.
            Denegarlo no impide la participación en las actividades.
          </p>
        </Section>

        <Section title="7. Destinatarios">
          <p>
            No cedemos los datos a terceros, salvo obligación legal. Para prestar el servicio nos apoyamos
            en proveedores que actúan como <strong>encargados del tratamiento</strong>:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Proveedor de alojamiento del sistema (servidor).</li>
            <li>
              <strong>Meta Platforms</strong> (WhatsApp Business Cloud API), para el envío del código de
              verificación, lo que puede implicar una <strong>transferencia internacional de datos</strong> fuera
              del EEE, amparada en las garantías previstas por la normativa (cláusulas contractuales tipo).
            </li>
          </ul>
        </Section>

        <Section title="8. Conservación">
          <p>
            Conservamos los datos de inscripción durante la temporada de prestación del servicio y mientras exista
            relación con la familia. Una vez finalizada, los bloqueamos durante los plazos legalmente exigibles
            para atender posibles responsabilidades y los suprimimos cuando estos transcurren.
          </p>
          <p>
            El justificante de inscripción (el documento en PDF con el resumen de la inscripción y los
            consentimientos otorgados) se conserva durante un máximo de 45 días desde su emisión, tras los
            cuales se elimina de forma automática.
          </p>
          <p>
            Las imágenes se conservan mientras no se revoque el consentimiento para su uso.
          </p>
        </Section>

        <Section title="9. Derechos">
          <p>
            Puedes ejercer tus derechos de <strong>acceso, rectificación, supresión, oposición, limitación
            y portabilidad</strong>, y <strong>retirar el consentimiento</strong> en cualquier momento,
            escribiendo a <strong>info@elclubdemanri.es</strong>. Si consideras que no se han atendido,
            puedes reclamar ante la <strong>Agencia Española de Protección de Datos</strong> (www.aepd.es).
          </p>
        </Section>

        <Section title="10. Seguridad">
          <p>
            Aplicamos medidas técnicas y organizativas: cifrado de las comunicaciones (HTTPS), control de
            acceso, verificación de identidad por código de un solo uso y limitación de los intentos de envío.
          </p>
        </Section>

        <Section title="11. Cambios">
          <p>
            Podremos actualizar esta política para adaptarla a cambios normativos u operativos.
            La versión vigente estará disponible en elclubdemanri.es.
          </p>
        </Section>
      </div>

      <Footer />
    </div>
  );
}
